<template>
    <div class="password-field" :class="{ 'has-floating-help': showLegend_ }">
        <div class="password-control">
            <input
                :type="passwordFieldType"
                v-model="password"
                :id="id"
                :data-test-id="dataTestId"
                @focus="inputFocus = true"
                @blur="inputFocus = false"
                class="form-control"
                :class="{ 'is-invalid': !isPasswordSecured }"
                autocomplete="new-password"
                :required="required" />
            <button type="button" class="password-toggle" :aria-label="toggleLabel" :title="toggleLabel" @click="switchVisibility">
                <feather class="feather-18" :type="featherIcon" />
            </button>
        </div>
        <div class="password-strength" :class="passwordStrengthClass">
            <div class="password-strength-header">
                <span>{{ $t("labels.password.strengthTitle") }}</span>
                <strong>{{ passwordStrengthLabel }}</strong>
            </div>
            <div class="password-strength-track" aria-hidden="true">
                <span :style="{ width: passwordStrengthWidth }"></span>
            </div>
        </div>
        <div v-if="passwordComplete" class="password-success" aria-live="polite">
            <feather type="check" />
            {{ $t("labels.password.secure") }}
        </div>
        <div class="div-pass-check password-checklist" v-show="showLegend_" aria-live="polite">
            <div class="password-rule" :class="atLeast12Chars ? 'is-valid' : 'is-missing'">
                <feather :type="atLeast12Chars ? 'check' : 'circle'" />
                {{ $tc("labels.password.minChars", 12) }}
            </div>
            <div class="password-rule" :class="atMost128Chars ? 'is-valid' : 'is-missing'">
                <feather :type="atMost128Chars ? 'check' : 'circle'" />
                {{ $tc("labels.password.maxChars", 128) }}
            </div>
            <div class="password-rule" :class="hasMin ? 'is-valid' : 'is-missing'">
                <feather :type="hasMin ? 'check' : 'circle'" />
                {{ $tc("labels.password.minLowerCaseChars", 1) }}
            </div>
            <div class="password-rule" :class="hasMaj ? 'is-valid' : 'is-missing'">
                <feather :type="hasMaj ? 'check' : 'circle'" />
                {{ $tc("labels.password.minUpperCaseChars", 1) }}
            </div>
            <div class="password-rule" :class="hasNumber ? 'is-valid' : 'is-missing'">
                <feather :type="hasNumber ? 'check' : 'circle'" />
                {{ $tc("labels.password.minNumbers", 1) }}
            </div>
            <div class="password-rule" :class="hasSpecialChar ? 'is-valid' : 'is-missing'">
                <feather :type="hasSpecialChar ? 'check' : 'circle'" />
                {{ $tc("labels.password.minSpecialChars", 1) }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            inputFocus: false,
            passwordFieldType: "password",
            featherIcon: "eye",
        };
    },
    props: {
        id: {
            type: String,
            default: undefined,
        },
        value: {
            default: null,
        },
        showLegend: {
            default: false,
        },
        hideLegend: {
            default: false,
        },
        dataTestId: {
            type: String,
            default: undefined,
        },
        required: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isPasswordSecured() {
            if (!this.password) return true;
            return /^(?=.*\p{Ll})(?=.*\p{Lu})(?=.*\p{N})(?=.*[\p{Z}\p{S}\p{P}]).{12,128}$/u.test(this.password);
        },
        atLeast12Chars() {
            if (!this.password) return false;
            return /^.{12,}$/u.test(this.password);
        },
        atMost128Chars() {
            if (!this.password) return false;
            return /^.{1,128}$/u.test(this.password);
        },
        hasMin() {
            if (!this.password) return false;
            return /\p{Ll}/u.test(this.password);
        },
        hasMaj() {
            if (!this.password) return false;
            return /\p{Lu}/u.test(this.password);
        },
        hasNumber() {
            if (!this.password) return false;
            return /\p{N}/u.test(this.password);
        },
        hasSpecialChar() {
            if (!this.password) return false;
            return /[\p{Z}\p{S}\p{P}]/u.test(this.password);
        },
        showLegend_() {
            return !this.hideLegend && !this.passwordComplete && (this.showLegend || this.inputFocus);
        },
        passwordComplete() {
            return !!this.password && this.isPasswordSecured;
        },
        passwordStrengthScore() {
            if (!this.password || !this.atMost128Chars) return 0;

            return [this.atLeast12Chars, this.hasMin, this.hasMaj, this.hasNumber, this.hasSpecialChar].filter(Boolean).length;
        },
        passwordStrengthClass() {
            if (!this.password) return "is-empty";
            if (this.passwordStrengthScore <= 2) return "is-weak";
            if (this.passwordStrengthScore === 3) return "is-fair";
            if (this.passwordStrengthScore === 4) return "is-good";
            return "is-strong";
        },
        passwordStrengthLabel() {
            if (!this.password) return this.$t("labels.password.strengthEmpty");
            if (!this.atMost128Chars) return this.$t("labels.password.strengthTooLong");
            if (this.passwordStrengthScore <= 2) return this.$t("labels.password.strengthWeak");
            if (this.passwordStrengthScore === 3) return this.$t("labels.password.strengthFair");
            if (this.passwordStrengthScore === 4) return this.$t("labels.password.strengthGood");
            return this.$t("labels.password.strengthStrong");
        },
        passwordStrengthWidth() {
            if (!this.password) return "0%";
            if (!this.atMost128Chars) return "100%";
            return `${(this.passwordStrengthScore / 5) * 100}%`;
        },
        toggleLabel() {
            return this.passwordFieldType === "password" ? this.$t("labels.password.show") : this.$t("labels.password.hide");
        },
        password: {
            get() {
                return this.value;
            },
            set(newVal) {
                this.$emit("input", newVal);
            },
        },
    },
    methods: {
        switchVisibility() {
            this.passwordFieldType = this.passwordFieldType === "password" ? "text" : "password";
            this.featherIcon = this.featherIcon === "eye" ? "eye-off" : "eye";
        },
    },
};
</script>

<style lang="scss" scoped>
.password-field {
    position: relative;
    width: 100%;
}

.password-field.has-floating-help {
    z-index: 30;
}

.password-control {
    position: relative;
}

.password-control .form-control {
    padding-right: 50px;
}

.password-control .form-control.is-invalid {
    padding-right: 86px;
    background-position: right 48px center;
}

.password-toggle {
    position: absolute;
    top: 50%;
    right: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    color: #083e60;
    background: transparent;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    transform: translateY(-50%);
    transition: color 0.18s ease, background-color 0.18s ease;
}

.password-toggle:hover,
.password-toggle:focus {
    color: #052a43;
    background-color: rgba(8, 62, 96, 0.08);
    outline: none;
}
</style>
